/**
 * ProtocolSection - "The Dravonix Protocol" (Who We Are)
 * ======================================================
 * 
 * ENHANCED VERSION:
 * - Dramatic entry animations
 * - IntersectionObserver for scroll trigger
 * - GSAP for continuous animations
 */

import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import ProtocolCard from './ProtocolCard';
import ProtocolParticles from './ProtocolParticles';
import './styles.css';

// Import dragon logo
import dragonLogo from '../../assets/dravonix_icon.png';

// Protocol data
const protocolData = [
  {
    id: 1,
    title: 'Secure Smart Contracts',
    description: 'We design',
    iconPath: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    position: 'top-left'
  },
  {
    id: 2,
    title: 'Blockchain Ecosystems',
    description: 'We build',
    iconPath: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
    position: 'top-right'
  },
  {
    id: 3,
    title: 'Scalable Software',
    description: 'We engineer',
    iconPath: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
    position: 'bottom-left'
  },
  {
    id: 4,
    title: 'Digital Brands',
    description: 'We accelerate',
    iconPath: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
    position: 'bottom-right'
  }
];

const ProtocolSection = () => {
  const sectionRef = useRef(null);
  const coreRef = useRef(null);
  const logoRef = useRef(null);
  const dottedRingRef = useRef(null);
  const titleRef = useRef(null);
  const badgeRef = useRef(null);
  const taglineRef = useRef(null);
  const linesRef = useRef(null);
  const cardRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [animationStarted, setAnimationStarted] = useState(false);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // IntersectionObserver for scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animationStarted) {
          setIsInView(true);
          setAnimationStarted(true);
        }
      },
      { threshold: 0.2, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [animationStarted]);

  // Entry animations when in view
  useEffect(() => {
    if (!isInView) return;

    // Animate badge
    gsap.fromTo(badgeRef.current,
      { opacity: 0, y: 50, scale: 0.8 },
      { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'back.out(1.7)' }
    );

    // Animate title with delay
    gsap.fromTo(titleRef.current,
      { opacity: 0, y: 60 },
      { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: 'power3.out' }
    );

    // Animate core with dramatic scale
    gsap.fromTo(coreRef.current,
      { opacity: 0, scale: 0, rotation: -180 },
      { 
        opacity: 1, 
        scale: 1, 
        rotation: 0, 
        duration: 1.2, 
        delay: 0.4, 
        ease: 'elastic.out(1, 0.5)' 
      }
    );

    // Animate SVG lines
    if (linesRef.current) {
      const lines = linesRef.current.querySelectorAll('.protocol-line');
      gsap.fromTo(lines,
        { strokeDashoffset: 400 },
        { 
          strokeDashoffset: 0, 
          duration: 1.5, 
          delay: 0.8, 
          stagger: 0.15,
          ease: 'power2.out'
        }
      );
    }

    // Animate cards with stagger
    cardRefs.current.forEach((card, index) => {
      if (card) {
        const directions = {
          'top-left': { x: -100, y: -50 },
          'top-right': { x: 100, y: -50 },
          'bottom-left': { x: -100, y: 50 },
          'bottom-right': { x: 100, y: 50 }
        };
        const pos = protocolData[index].position;
        const dir = directions[pos] || { x: 0, y: 50 };
        
        gsap.fromTo(card,
          { opacity: 0, x: dir.x, y: dir.y, scale: 0.8 },
          { 
            opacity: 1, 
            x: 0, 
            y: 0, 
            scale: 1, 
            duration: 0.8, 
            delay: 1 + index * 0.15,
            ease: 'back.out(1.4)'
          }
        );
      }
    });

    // Animate tagline
    gsap.fromTo(taglineRef.current,
      { opacity: 0, y: 30 },
      { opacity: 0.7, y: 0, duration: 0.6, delay: 1.8, ease: 'power2.out' }
    );

  }, [isInView]);

  // Continuous animations
  useEffect(() => {
    // Rotating dotted ring
    if (dottedRingRef.current) {
      gsap.to(dottedRingRef.current, {
        rotation: 360,
        duration: 30,
        repeat: -1,
        ease: 'none'
      });
    }

    // Pulsing glow
    gsap.to('.core-glow', {
      opacity: 0.9,
      scale: 1.2,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: 'power1.inOut'
    });

    // Floating logo
    if (logoRef.current) {
      gsap.to(logoRef.current, {
        y: -10,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut'
      });
    }

    // Rotating outer ring
    gsap.to('.core-ring-2', {
      rotation: -360,
      duration: 40,
      repeat: -1,
      ease: 'none'
    });

  }, []);

  return (
    <section className="protocol-section" ref={sectionRef} id="protocol">
      {/* Blockchain Background */}
      <div className="protocol-bg">
        <ProtocolParticles />
        <div className="blockchain-grid"></div>
        <div className="blockchain-nodes"></div>
        <div className="protocol-vignette"></div>
      </div>

      {/* Badge */}
      <div className="protocol-badge" ref={badgeRef} style={{ opacity: 0 }}>
        <span className="badge-dot"></span>
        <span>Who We Are</span>
      </div>

      {/* Section Title */}
      <h2 className="protocol-title" ref={titleRef} style={{ opacity: 0 }}>
        The Dravonix <span className="title-highlight">Protocol</span>
      </h2>

      {/* Core System Container */}
      <div className="core-system">
        
        {/* X-Pattern Connection Lines */}
        {!isMobile && (
          <svg 
            className="lines-svg" 
            viewBox="0 0 800 500" 
            preserveAspectRatio="xMidYMid meet"
            ref={linesRef}
          >
            <defs>
              <linearGradient id="lineGradientTL" x1="100%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#D4A853" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="lineGradientTR" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#D4A853" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="lineGradientBL" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D4A853" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
              </linearGradient>
              <linearGradient id="lineGradientBR" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#D4A853" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            
            <line className="protocol-line" x1="400" y1="250" x2="175" y2="75" stroke="url(#lineGradientTL)" strokeWidth="2" strokeDasharray="400" strokeDashoffset="400" />
            <line className="protocol-line" x1="400" y1="250" x2="625" y2="75" stroke="url(#lineGradientTR)" strokeWidth="2" strokeDasharray="400" strokeDashoffset="400" />
            <line className="protocol-line" x1="400" y1="250" x2="175" y2="425" stroke="url(#lineGradientBL)" strokeWidth="2" strokeDasharray="400" strokeDashoffset="400" />
            <line className="protocol-line" x1="400" y1="250" x2="625" y2="425" stroke="url(#lineGradientBR)" strokeWidth="2" strokeDasharray="400" strokeDashoffset="400" />
          </svg>
        )}

        {/* Central Core with Dragon Logo */}
        <div className="central-core" ref={coreRef} style={{ opacity: 0 }}>
          <div className="core-glow"></div>
          <div className="core-ring core-ring-1"></div>
          <div className="core-ring core-ring-2"></div>
          <div className="core-inner"></div>
          <div className="dragon-logo" ref={logoRef}>
            <img src={dragonLogo} alt="Dravonix" />
          </div>
          <div className="dotted-ring" ref={dottedRingRef}></div>
        </div>

        {/* Protocol Cards */}
        <div className="protocol-cards">
          {protocolData.map((protocol, index) => (
            <ProtocolCard
              key={protocol.id}
              ref={el => cardRefs.current[index] = el}
              title={protocol.title}
              description={protocol.description}
              iconPath={protocol.iconPath}
              position={protocol.position}
              style={{ opacity: 0 }}
            />
          ))}
        </div>
      </div>

      {/* Bottom Tagline */}
      <p className="protocol-tagline" ref={taglineRef} style={{ opacity: 0 }}>
        BUILDING TOMORROW'S DIGITAL INFRASTRUCTURE
      </p>
    </section>
  );
};

export default ProtocolSection;
